import { API_BASE_URL } from "@/services/api";
import type { Setting } from "@/services/setting";
import {
  normalizeCustomerDisplayPosters,
} from "@/utils/customerDisplayPosters";
import stateStore from "@/utils/store";

export const DEFAULT_RECEIPT_NOTE =
  "Thanks for shopping with us. Please come again.";

const toBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    if (["0", "false", "no", "off", ""].includes(normalized)) return false;
  }
  return fallback;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const buildAssetUrl = (path?: string | null): string => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  const base = API_BASE_URL.replace(/\/api\/?$/, "");
  const normalized = String(path).replace(/^\/+/, "");
  return `${base}/${normalized}`;
};

export const buildStorageAssetUrl = (path?: string | null): string => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  return buildAssetUrl(`storage/${String(path).replace(/^\/+/, "")}`);
};

export const normalizeSetting = (
  input?: Partial<Setting> | null
): Setting => ({
  id: input?.id,
  site_name: input?.site_name || "ProCare Solution",
  language: input?.language || "en",
  timezone: input?.timezone || "Asia/Phnom_Penh",
  dateformat: input?.dateformat || "Y-m-d",
  default_currency: input?.default_currency || "USD",
  default_tax_rate: toNullableNumber(input?.default_tax_rate),
  default_warehouse: toNullableNumber(input?.default_warehouse),
  default_customer: toNullableNumber(input?.default_customer),
  company_phone: input?.company_phone || "",
  company_email: input?.company_email || "",
  company_address: input?.company_address || "",
  logo: input?.logo || "",
  products_prefix: input?.products_prefix || "SKU-",
  supplier_prefix: input?.supplier_prefix || "SUP-",
  purchase_prefix: input?.purchase_prefix || "PUR-",
  purchaseReturn_prefix: input?.purchaseReturn_prefix || "PR-",
  sales_prefix: input?.sales_prefix || "SALE-",
  salesReturn_prefix: input?.salesReturn_prefix || "SR-",
  salesOrder_prefix: input?.salesOrder_prefix || "SO-",
  posinvoice_prefix: input?.posinvoice_prefix || "PINV",
  customer_prefix: input?.customer_prefix || "CT-",
  expense_prefix: input?.expense_prefix || "EX-",
  income_prefix: input?.income_prefix || "IN-",
  stockTransfer_prefix: input?.stockTransfer_prefix || "ST-",
  stockAdjustment_prefix: input?.stockAdjustment_prefix || "SA-",
  estimation_prefix: input?.estimation_prefix || "EST-",
  transaction_prefix: input?.transaction_prefix || "TRN-",
  employee_prefix: input?.employee_prefix || "EMP-",
  receipt_show_phone: toBoolean(input?.receipt_show_phone, true),
  receipt_show_address: toBoolean(input?.receipt_show_address, true),
  receipt_show_email: toBoolean(input?.receipt_show_email, false),
  receipt_show_customer: toBoolean(input?.receipt_show_customer, true),
  receipt_show_tax_discount: toBoolean(
    input?.receipt_show_tax_discount,
    true
  ),
  receipt_show_barcode: toBoolean(input?.receipt_show_barcode, false),
  receipt_show_note_to_customer: toBoolean(
    input?.receipt_show_note_to_customer,
    true
  ),
  receipt_note_to_customer:
    input?.receipt_note_to_customer || DEFAULT_RECEIPT_NOTE,
  customer_display_posters: normalizeCustomerDisplayPosters(
    input?.customer_display_posters
  ),
});

export const applyGlobalSettings = (setting: Setting) => {
  stateStore.setCurrencySymbol(
    setting.default_currency === "KHR" ? "៛" : "$"
  );
  stateStore.setSiteName(setting.site_name || "ProCare Solution");
  stateStore.setLogo(setting.logo || "");
  stateStore.setCompanyInfo({
    phone: setting.company_phone || "",
    email: setting.company_email || "",
    address: setting.company_address || "",
  });
  stateStore.setReferencePrefixes({
    products: setting.products_prefix || "SKU-",
    supplier: setting.supplier_prefix || "SUP-",
    purchase: setting.purchase_prefix || "PUR-",
    sales: setting.sales_prefix || "SALE-",
    pos: setting.posinvoice_prefix || "PINV",
    customer: setting.customer_prefix || "CT-",
    expense: setting.expense_prefix || "EX-",
    income: setting.income_prefix || "IN-",
    stockTransfer: setting.stockTransfer_prefix || "ST-",
    stockAdjustment: setting.stockAdjustment_prefix || "SA-",
    estimation: setting.estimation_prefix || "EST-",
    transaction: setting.transaction_prefix || "TRN-",
    employee: setting.employee_prefix || "EMP-",
  });
  stateStore.setReceiptSettings({
    showPhone: !!setting.receipt_show_phone,
    showAddress: !!setting.receipt_show_address,
    showEmail: !!setting.receipt_show_email,
    showCustomer: !!setting.receipt_show_customer,
    showTaxDiscount: !!setting.receipt_show_tax_discount,
    showBarcode: !!setting.receipt_show_barcode,
    showNoteToCustomer: !!setting.receipt_show_note_to_customer,
    noteToCustomer:
      setting.receipt_note_to_customer || DEFAULT_RECEIPT_NOTE,
  });
  stateStore.setCustomerDisplayPosters(
    normalizeCustomerDisplayPosters(setting.customer_display_posters)
  );
};
